// src/redux/flows/initializeSubscriptionStripePaymentFlow.ts

import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../store";
import Logger from "../helpers/logger";
import ErrorTracker from "../helpers/errorTracker";
import { useInitializeSubscriptionPaymentMutation } from "api/payments/paymentsApiSlice";
import { InitializeSubscriptionPaymentData } from "api/types";
import { stripePaymentInitialized, subscriptionCompleted } from "../slice/bookingCreation/bookingCreationSlice";
import { notifyActionableBookingFailure } from "../helpers/failureNotification";
import { buildAppUrl } from "../../util";

/**
 * Hook to initialize Stripe payment for new subscription
 * 
 * @remarks
 * This hook prepares the subscription data and calls the backend API
 * to create a Stripe checkout session for recurring subscription payment.
 * 
 * @returns Function to initialize Stripe subscription payment
 */
export const useInitializeSubscriptionStripePayment = () => {
  const selectedPlan = useSelector(
    (state: RootState) => state.subscription.newSubscription.selectedSubscriptionPlan
  );
  const hostData = useSelector(
    (state: RootState) => state.subscription.newSubscription.hostData
  );
  const selectedLocation = useSelector(
    (state: RootState) => state.subscription.newSubscription.selectedLocation
  );
  const appliedVoucher = useSelector(
    (state: RootState) => state.subscription.newSubscription.appliedVoucher
  );
  const [initializeSubscriptionPaymentMutation] = useInitializeSubscriptionPaymentMutation();

  const dispatch = useDispatch();

  const initializeSubscriptionStripePayment = async () => {
    try {
      if (!selectedPlan || !hostData) {
        Logger.error("Missing subscription data for payment initialization", {
          selectedPlan: selectedPlan?.id,
          hasHostData: !!hostData,
        });
        return null;
      }

      // Include {CHECKOUT_SESSION_ID} placeholder so Stripe appends the session_id to the return URL
      const returnUrl = buildAppUrl(
        "/payments/subscription/return?session_id={CHECKOUT_SESSION_ID}"
      );

      // Use pre-calculated price from voucher API response
      const finalPrice = appliedVoucher ? appliedVoucher.newAmount : selectedPlan.price;

      const payload: InitializeSubscriptionPaymentData = {
        commercialModelId: selectedPlan.id,
        contact: {
          firstname: hostData.firstName ?? "",
          lastname: hostData.lastName ?? "",
          email: hostData.email ?? "",
          telephone: hostData.phone ?? "",
        },
        buyingAsBusiness: false, // To change later when we add a checkbox for business purchases
        // startDate: new Date().toISOString(),
        voucherId: appliedVoucher?.voucherId || undefined,
        returnUrl: returnUrl,
        cancelUrl: buildAppUrl("/key-handover/subscription/summary"),
      };

      const response = await initializeSubscriptionPaymentMutation(
        payload
      ).unwrap();

      // Handle NO_PAYMENT case (voucher reduced price to 0)
      if (response && response.status === "NO_PAYMENT" && response.customerAgreementId) {
        // Store the subscription ID and mark payment as completed
        dispatch(subscriptionCompleted(response.customerAgreementId));
        return {
          ...response,
          skipCheckout: true, // Flag to skip Stripe redirect
        };
      }

      // Normal payment flow with Stripe checkout
      if (response) {
        dispatch(stripePaymentInitialized(response));
      }

      return response;

    } catch (error) {
      Logger.error("Failed to initialize subscription Stripe payment", error);
      ErrorTracker.trackException(error as Error);
      // SubscriptionPaymentPage renders the persistent failure panel; add the
      // reason only when it is one the customer can act on (#1970).
      notifyActionableBookingFailure(dispatch, error);
      return null;
    }
  };

  return initializeSubscriptionStripePayment;
};
